import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BsX, BsCheckCircleFill, BsCircle, BsTruck, BsChevronDown, BsChevronUp } from "react-icons/bs";

/**
 * The parcel popup: what is going on with it, and why it costs this.
 *
 * Rendered from the payload the explain endpoint produces — PriceExplanation
 * for the price, ParcelJourney under `journey` for everything else. Presentational
 * only; it never fetches. Customer panel, admin panel and the landing calculator
 * all hand it the same shape (the calculator has no journey and gets the price
 * section alone), so one parcel can never be explained two ways.
 */

const money = (n) => `${Number(n || 0).toFixed(2)} ₾`;
const kg = (n) => Number(n || 0).toFixed(Number(n) % 1 === 0 ? 0 : 2);

// ISO / MySQL timestamps → "dd.mm.yyyy hh:mm"; OnWay's own "dd.mm.yyyy hh:mm"
// strings pass through untouched.
const when = (v) => {
   if (!v) return "";
   if (/^\d{2}\.\d{2}\.\d{4}/.test(v)) return v;
   const d = new Date(String(v).replace(" ", "T"));
   if (isNaN(d)) return String(v);
   const p = (x) => String(x).padStart(2, "0");
   const hasTime = /\d{2}:\d{2}/.test(String(v));
   return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}${
      hasTime ? ` ${p(d.getHours())}:${p(d.getMinutes())}` : ""
   }`;
};

const Section = ({ title, children }) => (
   <div className="mt-4">
      <p className="text-[11px] uppercase tracking-wide font-bold text-slate-400 dark:text-slate-500 mb-1.5">
         {title}
      </p>
      {children}
   </div>
);

const Row = ({ label, value, muted }) => (
   <div className="flex items-baseline justify-between gap-4 text-sm sm:text-xs">
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
      <span
         className={`font-semibold whitespace-nowrap text-right ${
            muted ? "text-slate-400 dark:text-slate-500" : "text-slate-800 dark:text-slate-100"
         }`}
      >
         {value}
      </span>
   </div>
);

/* ------------------------------------------------------------------ */
/* what is going on                                                     */
/* ------------------------------------------------------------------ */

const Journey = ({ j }) => {
   const { t } = useTranslation();
   const [showLog, setShowLog] = useState(false);

   const d = j.delivery || {};
   const onway = d.onway;
   const charge = d.charge;
   const pay = j.payment || {};
   const customs = j.customs || {};

   const customsNote = {
      not_needed: t("customs_not_needed"),
      must_declare: t("customs_must_declare"),
      declared: t("customs_declared"),
      in_customs: t("customs_in_customs"),
   }[customs.state];

   const deliveryNote = (() => {
      if (j.stage === "delivered") {
         return {
            courier: t("delivered_by_courier"),
            address: t("delivered_to_address"),
            person: `${t("delivered_to_person")}${d.person ? ` — ${d.person}` : ""}`,
            recipient: t("delivered_to_recipient"),
         }[d.mode];
      }
      return t("delivery_by_courier");
   })();

   return (
      <>
         {/* --- the timeline ------------------------------------------ */}
         <Section title={t("whats_going_on")}>
            <ol className="relative border-l border-slate-200 dark:border-slate-600 ml-2 pl-4 flex flex-col gap-2">
               {j.steps.map((s) => (
                  <li key={s.key} className="relative">
                     <span className="absolute -left-[22px] top-0.5 bg-white dark:bg-slate-800">
                        {s.done ? (
                           <BsCheckCircleFill className="w-3.5 h-3.5 text-teal_600 dark:text-teal-400" />
                        ) : (
                           <BsCircle className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600" />
                        )}
                     </span>
                     <div className="flex items-baseline justify-between gap-3 text-sm sm:text-xs">
                        <span
                           className={
                              s.done
                                 ? "text-slate-800 dark:text-slate-100"
                                 : "text-slate-400 dark:text-slate-500"
                           }
                        >
                           {t(s.estimated && !s.done ? "step_arrived_est" : `step_${s.key}`)}
                        </span>
                        {s.at && (
                           <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                              {when(s.at)}
                           </span>
                        )}
                     </div>
                  </li>
               ))}
            </ol>
         </Section>

         {/* --- the other parcels on this flight ----------------------- */}
         {j.flight && j.flight.count > 1 && (
            <Section title={`${t("flight_title")} · ${j.flight.code}`}>
               <div className="rounded-2xl bg-slate-50 dark:bg-slate-700/60 border border-slate-200 dark:border-slate-600 p-3 flex flex-col gap-1">
                  {j.flight.packs.map((p) => (
                     <div
                        key={p.id}
                        className={`flex items-baseline justify-between gap-3 text-xs ${
                           p.this ? "font-bold text-slate-900 dark:text-white" : "text-slate-600 dark:text-slate-300"
                        }`}
                     >
                        <span className="truncate">
                           {p.tracking}
                           {p.this && (
                              <span className="ml-1 font-normal text-teal_600 dark:text-teal-400">· {t("flight_this")}</span>
                           )}
                        </span>
                        <span className="whitespace-nowrap">
                           {kg(p.weight)} {t("kg")}
                           <span className="inline-block w-16 text-right">{money(p.price)}</span>
                        </span>
                     </div>
                  ))}
                  <div className="mt-1 pt-1.5 border-t border-slate-200 dark:border-slate-600 flex items-baseline justify-between text-xs font-bold text-slate-800 dark:text-slate-100">
                     <span>
                        {t("flight_total")} · {j.flight.count} {t("parcels").toLowerCase()}
                     </span>
                     <span className="whitespace-nowrap">
                        {kg(j.flight.total_weight)} {t("kg")}
                        <span className="inline-block w-16 text-right">{money(j.flight.total_price)}</span>
                     </span>
                  </div>
                  {j.flight.total_due > 0 && j.flight.total_due !== j.flight.total_price && (
                     <Row label={t("flight_due")} value={money(j.flight.total_due)} />
                  )}

                  <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug mt-1">
                     {j.flight.model === "standard"
                        ? t("flight_tier_note", { total: kg(j.flight.total_weight), tier: j.flight.tier })
                        : t("flight_home_note", { total: kg(j.flight.total_weight) })}
                  </p>
                  {j.flight.courier_fee_estimate !== null && j.flight.courier_fee_estimate !== undefined && (
                     <Row
                        label={t("flight_fee_estimate")}
                        value={j.flight.courier_fee_estimate > 0 ? money(j.flight.courier_fee_estimate) : t("delivery_fee_free")}
                     />
                  )}
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">
                     {j.flight.same_address ? t("flight_together") : t("flight_split_address")}
                  </p>
               </div>
            </Section>
         )}

         {/* --- customs ----------------------------------------------- */}
         <Section title={t("customs_title")}>
            <p className="text-sm sm:text-xs text-slate-700 dark:text-slate-200">{customsNote}</p>
            <p
               className={`text-xs mt-0.5 ${
                  customs.declared
                     ? "text-slate-500 dark:text-slate-400"
                     : "text-amber-700 dark:text-amber-300"
               }`}
            >
               {customs.declared ? t("declaration_ok") : t("declaration_missing")}
            </p>
         </Section>

         {/* --- delivery ---------------------------------------------- */}
         <Section title={t("delivery_title")}>
            <div className="rounded-2xl bg-slate-50 dark:bg-slate-700/60 border border-slate-200 dark:border-slate-600 p-3 flex flex-col gap-1.5">
               <div className="flex items-start gap-2">
                  <BsTruck className="w-4 h-4 mt-0.5 shrink-0 text-teal_600 dark:text-teal-400" />
                  <div className="min-w-0">
                     <p className="text-sm sm:text-xs text-slate-800 dark:text-slate-100">{deliveryNote}</p>
                     {d.address && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 break-words">{d.address}</p>
                     )}
                     {d.delivered_at && (
                        <p className="text-xs text-slate-400 dark:text-slate-500">{when(d.delivered_at)}</p>
                     )}
                  </div>
               </div>

               {charge && (
                  <div className="mt-1 pt-2 border-t border-slate-200 dark:border-slate-600 flex flex-col gap-1">
                     <Row
                        label={`${t("delivery_fee")} · ${charge.trackings.length} ${t("parcels").toLowerCase()} · ${kg(
                           charge.weight_kg
                        )} ${t("kg")}`}
                        value={charge.amount > 0 ? money(charge.amount) : t("delivery_fee_free")}
                     />
                     {charge.amount > 0 && (
                        <Row
                           label={charge.due > 0 ? t("payment_due") : t("payment_paid_full")}
                           value={charge.due > 0 ? money(charge.due) : money(charge.paid)}
                           muted={charge.due <= 0}
                        />
                     )}
                     <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-snug">
                        {t("delivery_group_note")}
                     </p>
                  </div>
               )}

               {onway && (
                  <div className="mt-1 pt-2 border-t border-slate-200 dark:border-slate-600 flex flex-col gap-1">
                     <Row
                        label={t("courier_order")}
                        value={onway.tracking || "—"}
                     />
                     <Row
                        label={t("courier_status")}
                        value={
                           onway.cancelled
                              ? t("courier_cancelled")
                              : `${onway.status || "—"}${
                                   onway.status_changed_at ? ` · ${when(onway.status_changed_at)}` : ""
                                }`
                        }
                     />
                     {onway.comment && (
                        <p className="text-xs text-amber-700 dark:text-amber-300">{onway.comment}</p>
                     )}
                     {!onway.cancelled && !onway.delivered && onway.expected_date && (
                        <Row label={t("courier_expected")} value={onway.expected_date} />
                     )}
                     {!onway.cancelled && onway.cod_amount > 0 && (
                        <Row
                           label={t("courier_cod")}
                           value={money(onway.cod_amount)}
                           muted={onway.delivered}
                        />
                     )}
                     {onway.packs?.length > 1 && (
                        <p className="text-[11px] text-slate-400 dark:text-slate-500 break-all">
                           {t("courier_grouped_with")} {onway.packs.join(", ")}
                        </p>
                     )}
                     {onway.history?.length > 0 && (
                        <>
                           <button
                              type="button"
                              onClick={() => setShowLog((v) => !v)}
                              className="self-start text-xs text-teal_600 dark:text-teal-400 font-bold flex items-center gap-1 mt-1"
                           >
                              {t("courier_history")} {showLog ? <BsChevronUp /> : <BsChevronDown />}
                           </button>
                           {showLog && (
                              <ol className="flex flex-col gap-1 mt-1">
                                 {onway.history.map((h, i) => (
                                    <li key={i} className="text-xs flex items-baseline justify-between gap-3">
                                       <span className="text-slate-700 dark:text-slate-200">
                                          {h.status}
                                          {h.comment && (
                                             <span className="text-slate-400 dark:text-slate-500"> · {h.comment}</span>
                                          )}
                                       </span>
                                       <span className="text-slate-400 dark:text-slate-500 whitespace-nowrap">{h.at}</span>
                                    </li>
                                 ))}
                              </ol>
                           )}
                        </>
                     )}
                  </div>
               )}
            </div>
         </Section>

         {/* --- payment ----------------------------------------------- */}
         <Section title={t("payment_title")}>
            <div className="flex flex-col gap-1">
               <Row label={t("total_to_pay")} value={money(pay.price)} />
               <Row
                  label={
                     pay.pay_type === "onway_cod"
                        ? t("paid_via_courier")
                        : pay.due > 0
                        ? t("payment_due")
                        : t("payment_paid_full")
                  }
                  value={pay.due > 0 ? money(pay.due) : money(pay.paid)}
                  muted={pay.due <= 0}
               />
               {pay.paid_at && pay.due <= 0 && (
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 -mt-0.5">{when(pay.paid_at)}</p>
               )}
               {pay.cod_locked && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-100 dark:border-amber-800 rounded-lg px-3 py-2 leading-snug mt-1">
                     {t("payment_cod_locked")}
                  </p>
               )}
            </div>
         </Section>

         {/* --- what the customer still has to do ----------------------- */}
         {j.stage !== "delivered" && (
            <Section title={t("todo_title")}>
               {j.todo.length === 0 ? (
                  <p className="text-sm sm:text-xs text-teal-700 dark:text-teal-300">{t("todo_none")}</p>
               ) : (
                  <ul className="flex flex-col gap-1">
                     {j.todo.map((k) => (
                        <li key={k} className="text-sm sm:text-xs text-slate-800 dark:text-slate-100 flex gap-2">
                           <span className="text-amber-500">●</span>
                           {t(`todo_${k}`)}
                        </li>
                     ))}
                  </ul>
               )}
            </Section>
         )}
      </>
   );
};

/* ------------------------------------------------------------------ */
/* why it costs this                                                    */
/* ------------------------------------------------------------------ */

const Price = ({ explain }) => {
   const { t } = useTranslation();
   const w = explain.weight || {};
   const rate = explain.rate || {};
   const lines = explain.lines || [];
   const usedVolume = w.used === "volume";

   const volumeNote = (() => {
      if (usedVolume) return t("volume_used_note");
      if (w.volume_skipped_reason === "below_min_side")
         return t("volume_not_used_small", { cm: kg(w.min_side_cm), longest: kg(w.longest_side) });
      if (w.volume_skipped_reason === "repack") return t("volume_not_used_repack");
      if (w.volume_skipped_reason === "no_dimensions") return t("volume_not_used_nodim");
      return null;
   })();

   const rateNote = (() => {
      if (rate.source === "scheduled") return t("scheduled_rate_note");
      if (explain.model === "home_delivery")
         return rate.bracket ? t("bracket_note", { bracket: rate.bracket }) : null;
      if (!rate.bracket) return null;
      return rate.group_is_wider
         ? t("tier_note", { tier: rate.bracket, group: kg(rate.group_weight) })
         : t("tier_note_single", { tier: rate.bracket });
   })();

   return (
      <>
         <div className="mt-2 rounded-2xl bg-teal-50 dark:bg-teal-900/30 border border-teal-100 dark:border-teal-800 p-4 sm:p-3">
            <div className="flex items-baseline justify-between gap-3">
               <span className="text-sm sm:text-xs text-teal-800 dark:text-teal-200">{t("billed_weight")}</span>
               <span className="font-bold text-teal-900 dark:text-teal-100">
                  {kg(w.chargeable)} {t("kg")}{" "}
                  <span className="font-normal text-xs">
                     ({usedVolume ? t("billed_on_volume") : t("billed_on_real")})
                  </span>
               </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-teal-700 dark:text-teal-300">
               <span>
                  {t("real_weight")}: {kg(w.real)} {t("kg")}
               </span>
               <span>
                  {t("volume_weight")}: {kg(w.volume)} {t("kg")}
               </span>
               {w.dimensions && (
                  <span>
                     {w.dimensions.x} × {w.dimensions.y} × {w.dimensions.z} {t("cm")}
                  </span>
               )}
            </div>
            {volumeNote && (
               <p className="mt-2 text-xs text-teal-800 dark:text-teal-200 leading-snug">{volumeNote}</p>
            )}
            <p className="mt-1 text-[11px] text-teal-600 dark:text-teal-400 leading-snug">{t("volume_formula")}</p>
         </div>

         <div className="mt-4 flex flex-col gap-1">
            <Row label={t("price_per_kg")} value={`${Number(rate.per_kg_gel || 0).toFixed(2)} ₾`} />
            {!!rate.currency_to_gel && !!rate.per_kg_foreign && (
               <p className="text-[11px] text-slate-400 dark:text-slate-500 -mt-0.5">
                  {rate.per_kg_foreign} {rate.currency} × {rate.currency_to_gel} ({t("rate_used")} {rate.currency}→GEL)
               </p>
            )}
            {rateNote && <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug mt-1">{rateNote}</p>}
            {explain.model === "home_delivery" && (
               <p className="text-xs text-slate-500 dark:text-slate-400">
                  {rate.zone === "highland" ? t("zone_highland") : t("zone_tbilisi")}
                  {rate.city ? ` · ${rate.city}` : ""}
               </p>
            )}
         </div>

         <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-600 flex flex-col gap-1.5">
            {lines.map((l) => {
               const label =
                  l.key === "discount" && l.percent
                     ? `${t("line_discount")} (${l.percent}%)`
                     : l.key === "promo" && l.code
                     ? `${t("line_promo")} · ${l.code}`
                     : t(`line_${l.key}`);
               return (
                  <Row
                     key={l.key}
                     label={label}
                     value={`${l.amount < 0 ? "−" : ""}${money(Math.abs(l.amount))}`}
                  />
               );
            })}
         </div>

         <div className="mt-3 pt-3 border-t-2 border-slate-200 dark:border-slate-600 flex items-baseline justify-between">
            <span className="font-bold text-sm dark:text-slate-100">{t("total_to_pay")}</span>
            <span className="font-bold text-lg text-teal_600 dark:text-teal-400">
               {money(explain.charged ?? explain.total)}
            </span>
         </div>

         {explain.locked && (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-100 dark:border-amber-800 rounded-lg px-3 py-2 leading-snug">
               {t("price_locked_note")}
            </p>
         )}
         {!explain.locked && explain.matches_charged === false && (
            <p className="mt-2 text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 leading-snug">
               {t("price_stale_note", {
                  charged: Number(explain.charged || 0).toFixed(2),
                  computed: Number(explain.total || 0).toFixed(2),
               })}
            </p>
         )}
      </>
   );
};

/* ------------------------------------------------------------------ */

const PriceExplainModal = ({ explain, onClose }) => {
   const { t } = useTranslation();
   // Price is the whole story on the landing calculator; on a real parcel it
   // comes after what is going on, folded so the timeline stays on top.
   const [showPrice, setShowPrice] = useState(!explain?.journey);
   if (!explain) return null;

   const j = explain.journey;

   return (
      <div
         className="w-screen h-screen bg-white bg-opacity-80 dark:bg-slate-900 dark:bg-opacity-80 flex items-center justify-center fixed top-0 left-0 z-50 px-4"
         onClick={onClose}
      >
         <div
            className="bg-white dark:bg-slate-800 flex flex-col w-[560px] max-w-full max-h-[88vh] overflow-auto scrollbar p-6 sm:p-4 border border-gray-300 dark:border-slate-600 shadow-lg rounded-[24px] relative"
            onClick={(e) => e.stopPropagation()}
         >
            <BsX onClick={onClose} className="absolute top-4 right-4 text-gray-400 w-6 h-6 cursor-pointer" />

            <h2 className="font-bold text-lg sm:text-base pr-8 dark:text-slate-100">
               {j ? t("parcel_details") : t("how_price_calculated")}
            </h2>
            {explain.tracking_number && (
               <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 break-all">
                  {explain.tracking_number}
                  {explain.shipment_code ? ` · ${explain.shipment_code}` : ""}
               </p>
            )}

            {j && <Journey j={j} />}

            {j ? (
               <div className="mt-5 pt-3 border-t border-slate-200 dark:border-slate-600">
                  <button
                     type="button"
                     onClick={() => setShowPrice((v) => !v)}
                     className="w-full flex items-center justify-between text-sm font-bold text-slate-800 dark:text-slate-100"
                  >
                     <span>
                        {t("how_price_calculated")}
                        <span className="font-normal text-slate-400 dark:text-slate-500"> · {money(explain.charged ?? explain.total)}</span>
                     </span>
                     {showPrice ? <BsChevronUp /> : <BsChevronDown />}
                  </button>
                  {showPrice && <Price explain={explain} />}
               </div>
            ) : (
               <Price explain={explain} />
            )}
         </div>
      </div>
   );
};

export default PriceExplainModal;
